import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {
  RiAddLine,
  RiEditLine,
  RiDeleteBinLine,
  RiArrowLeftSLine,
  RiArrowRightSLine,
  RiCloseLine,
  RiSearchLine,
  RiQuestionLine,
  RiCalendarLine,
} from "react-icons/ri";
import toast from "react-hot-toast";
import { confirmAndDelete } from "../../utils/confirmAndDelete";
import { HelpCircle, MessageSquare } from "lucide-react";

const CardSkeleton = () => (
  <div className="bg-white rounded-2xl shadow-lg border p-6 animate-pulse">
    <div className="flex items-start justify-between mb-4">
      <div className="flex-1">
        <div className="h-6 bg-gray-200 rounded w-3/4 mb-3"></div>
        <div className="h-4 bg-gray-200 rounded w-full"></div>
        <div className="h-4 bg-gray-200 rounded w-5/6 mt-2"></div>
      </div>
      <div className="flex space-x-2 ml-4">
        <div className="h-8 w-8 bg-gray-200 rounded-lg"></div>
        <div className="h-8 w-8 bg-gray-200 rounded-lg"></div>
      </div>
    </div>
  </div>
);

const AdminFaqs = () => {
  const [activeTab, setActiveTab] = useState("all-faqs");
  const [faqs, setFaqs] = useState([]);
  const [askedQuestions, setAskedQuestions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [formData, setFormData] = useState({
    question: "",
    answer: "",
  });

  const itemsPerPage = 6;

  const fetchFaqs = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/faqs/get-all-faqs`
      );
      setFaqs(res.data.data || []);
    } catch (err) {
      console.error(err);
      setFaqs([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAskedQuestions = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/faq-inquiry/get-all-inquiries`
      );
      if (res.data.success) {
        setAskedQuestions(res.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching asked questions:", error);
      toast.error("Failed to fetch asked questions");
      setAskedQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "all-faqs") {
      fetchFaqs();
    } else {
      fetchAskedQuestions();
    }
    setCurrentPage(1);
  }, [activeTab]);

  const currentData = activeTab === "all-faqs" ? faqs : askedQuestions;

  const filteredData = currentData.filter((item) => {
    const matchesSearch =
      searchQuery === "" ||
      item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.answer &&
        item.answer.toLowerCase().includes(searchQuery.toLowerCase()));

    return matchesSearch;
  });

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirst, indexOfLast);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const openNewModal = () => {
    setEditingFaq(null);
    setFormData({
      question: "",
      answer: "",
    });
    setIsModalOpen(true);
  };

  const openEditModal = (faq) => {
    setEditingFaq(faq);
    setFormData({
      question: faq.question,
      answer: faq.answer,
    });
    setIsModalOpen(true);
  };

  const deleteFaq = async (_id) => {
    const confirmed = await confirmAndDelete({
      id: _id,
      url: `${process.env.REACT_APP_API_URL}/faqs/delete-faq`,
    });

    if (confirmed) {
      const updated = faqs.filter((f) => f._id !== _id);
      setFaqs(updated);

      const totalPagesAfterDelete = Math.ceil(updated.length / itemsPerPage);
      if (currentPage > totalPagesAfterDelete) {
        setCurrentPage(totalPagesAfterDelete || 1);
      }

      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "The FAQ has been deleted successfully.",
        confirmButtonColor: "#10B981",
        background: "#fff",
      });
    }
  };

  const deleteAskedQuestion = async (id) => {
    const isConfirmed = await confirmAndDelete({
      id,
      url: `${process.env.REACT_APP_API_URL}/faq-inquiry/delete-inquiry`,
    });

    if (isConfirmed) {
      try {
        await fetchAskedQuestions();
        const newTotalPages = Math.ceil(
          (askedQuestions.length - 1) / itemsPerPage
        );
        if (currentPage > newTotalPages) {
          setCurrentPage(newTotalPages || 1);
        }

        Swal.fire({
          title: "Deleted!",
          text: "The question has been deleted.",
          icon: "success",
          confirmButtonColor: "#10B981",
        });
      } catch (err) {
        console.error("Error deleting question:", err);
        Swal.fire({
          title: "Error!",
          text: "Something went wrong.",
          icon: "error",
          confirmButtonColor: "#EF4444",
        });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);

      const payload = {
        question: formData.question,
        answer: formData.answer,
      };

      if (editingFaq) {
        await axios.patch(
          `${process.env.REACT_APP_API_URL}/faqs/update-faq/${editingFaq._id}`,
          payload
        );
        toast.success("FAQ updated successfully!");
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/faqs/create-faq`,
          payload
        );
        toast.success("FAQ created successfully!");
      }
      setIsModalOpen(false);
      fetchFaqs();
    } catch (err) {
      console.error(err);
      toast.error("Failed to save FAQ");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  return (
    <div className="min-h-screen bg-transparent p-6 tracking-wide">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-heading font-bold bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor bg-clip-text text-transparent">
          FAQ Management
        </h1>
        {activeTab === "all-faqs" && (
          <button
            onClick={openNewModal}
            className="bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor text-white py-2 px-4 rounded-xl flex items-center shadow-lg hover:shadow-xl transition-shadow"
          >
            <RiAddLine className="mr-2" />
            Add FAQ
          </button>
        )}
      </div>

      <div className="flex justify-start space-x-6 border-b border-gray-200 mb-8">
        <button
          onClick={() => setActiveTab("all-faqs")}
          className={`flex items-center gap-2 pb-3 px-2 text-base font-semibold transition-all ${
            activeTab === "all-faqs"
              ? "text-primaryBtnColor border-b-2 border-primaryBtnColor"
              : "text-gray-500 hover:text-primaryBtnHoverColor"
          }`}
        >
          <HelpCircle size={18} />
          All FAQs
        </button>

        <button
          onClick={() => setActiveTab("asked-questions")}
          className={`flex items-center gap-2 pb-3 px-2 text-base font-semibold transition-all ${
            activeTab === "asked-questions"
              ? "text-primaryBtnColor border-b-2 border-primaryBtnColor"
              : "text-gray-500 hover:text-primaryBtnHoverColor"
          }`}
        >
          <MessageSquare size={18} />
          Asked Questions
        </button>
      </div>

      {/* Search Section */}
      <div className="bg-white rounded-2xl shadow-lg border border-primaryTextColor p-6 mb-8">
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-semibold text-secondaryTextColor mb-2">
              Search {activeTab === "all-faqs" ? "FAQs" : "Questions"}
            </label>
            <div className="relative">
              <RiSearchLine className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder={`Search by question${
                  activeTab === "all-faqs" ? " or answer" : ""
                }...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full border border-gray-300 rounded-lg p-3 pl-10 focus:outline-none focus:ring-2 focus:ring-primaryBtnColor"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Stats Card */}
      <div className="bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor rounded-2xl shadow-lg p-6 mb-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold mb-1">
              Total {activeTab === "all-faqs" ? "FAQs" : "Asked Questions"}
            </h3>
            <p className="text-3xl font-bold">{filteredData.length}</p>
          </div>
          <div className="bg-white bg-opacity-20 p-4 rounded-full">
            <RiQuestionLine size={32} />
          </div>
        </div>
      </div>

      {/* Content List */}
      <div className="grid grid-cols-1 gap-6 mb-8">
        {loading ? (
          Array.from({ length: itemsPerPage }).map((_, i) => (
            <CardSkeleton key={i} />
          ))
        ) : currentItems.length > 0 ? (
          currentItems.map((item) => (
            <div
              key={item._id}
              className="bg-white rounded-2xl shadow-lg border border-primaryTextColor p-6 hover:shadow-xl transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {/* Question */}
                  <div className="flex items-start">
                    <div className="bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor text-white rounded-full p-2 mr-3 mt-1 flex-shrink-0">
                      <RiQuestionLine size={20} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-primaryTextColor font-heading mb-2">
                        {item.question}
                      </h3>
                      {activeTab === "all-faqs" && item.answer && (
                        <p className="text-sm text-gray-600 leading-relaxed">
                          {item.answer}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-2 ml-4 flex-shrink-0">
                  {activeTab === "all-faqs" && (
                    <button
                      onClick={() => openEditModal(item)}
                      className="p-2 bg-gray-100 rounded-lg text-primaryBtnHoverColor hover:bg-primaryBtnColor hover:text-white transition-colors"
                    >
                      <RiEditLine size={18} />
                    </button>
                  )}
                  <button
                    onClick={() =>
                      activeTab === "all-faqs"
                        ? deleteFaq(item._id)
                        : deleteAskedQuestion(item._id)
                    }
                    className="p-2 bg-gray-100 rounded-lg text-red-500 hover:bg-red-500 hover:text-white transition-colors"
                  >
                    <RiDeleteBinLine size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full text-center py-12">
            <div className="bg-white rounded-2xl shadow-lg border border-primaryTextColor p-12">
              <RiQuestionLine
                className="mx-auto text-gray-300 mb-4"
                size={64}
              />
              <p className="text-gray-500 text-lg">
                No {activeTab === "all-faqs" ? "FAQs" : "questions"} found
              </p>
              <p className="text-gray-400 text-sm mt-2">
                {searchQuery
                  ? "Try adjusting your search terms"
                  : activeTab === "all-faqs"
                  ? "Click 'Add FAQ' to create your first question"
                  : "Asked questions will appear here when users submit them"}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center space-x-4">
          <button
            onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
            className={`p-2 rounded-lg ${
              currentPage === 1
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : "bg-primaryBtnColor text-white hover:bg-primaryBtnHoverColor"
            }`}
          >
            <RiArrowLeftSLine size={20} />
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={`px-3 py-1 rounded-lg ${
                currentPage === page
                  ? "bg-primaryBtnColor text-white"
                  : "bg-gray-200 text-secondaryTextColor hover:bg-gray-300"
              }`}
            >
              {page}
            </button>
          ))}
          <button
            onClick={() =>
              currentPage < totalPages && setCurrentPage(currentPage + 1)
            }
            disabled={currentPage === totalPages}
            className={`p-2 rounded-lg ${
              currentPage === totalPages
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : "bg-primaryBtnColor text-white hover:bg-primaryBtnHoverColor"
            }`}
          >
            <RiArrowRightSLine size={20} />
          </button>
        </div>
      )}

      {/* Modal for Add/Edit FAQ */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl border border-primaryTextColor shadow-xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
              <h2 className="text-xl font-bold text-primaryTextColor">
                {editingFaq ? "Edit FAQ" : "Add New FAQ"}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-500 hover:text-primaryBtnHoverColor"
              >
                <RiCloseLine size={24} />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Question */}
              <div>
                <label className="block text-sm font-semibold text-secondaryTextColor mb-2">
                  Question *
                </label>
                <input
                  type="text"
                  name="question"
                  value={formData.question}
                  onChange={handleChange}
                  required
                  placeholder="Enter your question here..."
                  className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-primaryBtnColor"
                />
              </div>

              {/* Answer */}
              <div>
                <label className="block text-sm font-semibold text-secondaryTextColor mb-2">
                  Answer *
                </label>
                <textarea
                  name="answer"
                  value={formData.answer}
                  onChange={handleChange}
                  required
                  placeholder="Enter the answer here..."
                  className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-primaryBtnColor"
                  rows="6"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="bg-gray-200 text-secondaryTextColor py-2 px-6 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  disabled={isSubmitting}
                  type="submit"
                  className="bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor text-white w-40 flex items-center justify-center py-2 px-6 rounded-lg hover:shadow-lg transition-shadow disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <div className="spinner w-full"></div>
                  ) : editingFaq ? (
                    "Update FAQ"
                  ) : (
                    "Add FAQ"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFaqs;
